import { forwardRef, useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

// 21-oct, macOS 27: campo de 24 con filo fino y anillo de foco del acento (3,5 al 50 %). En el
// teléfono, 44 de alto y letra 16 (con menos, el iPhone agranda la página al tocar el campo).
export const campoMac =
  'w-full rounded-[10px] border-0 bg-white px-3 text-base text-slate-900 caret-acento ' +
  'shadow-[0_0_0_1px_var(--mac-campo-filo)] placeholder:text-slate-400 transition-shadow ' +
  'focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white/5 dark:text-slate-100 ' +
  'lateral:rounded-[5px] lateral:px-2 lateral:text-sm';
const baseField = `h-11 lateral:h-6 ${campoMac}`;

/** Campo de texto con label, error y toggle show/hide para password. */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, id, type = 'text', ...props }, ref) => {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const [reveal, setReveal] = useState(false);
    const isPassword = type === 'password';
    const resolvedType = isPassword && reveal ? 'text' : type;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={fieldId} className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={fieldId}
            type={resolvedType}
            className={cn(
              baseField,
              isPassword && 'pr-10',
              error && 'shadow-[0_0_0_1px_var(--mac-rojo)] focus:ring-[var(--mac-rojo)]/40',
              className,
            )}
            aria-invalid={Boolean(error)}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="absolute right-0 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 lateral:h-6 lateral:w-7"
              tabIndex={-1}
              aria-label={reveal ? 'Ocultar' : 'Mostrar'}
            >
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {error ? (
          <p className="mt-1 text-xs text-danger">{error}</p>
        ) : hint ? (
          <p className="mt-1 text-xs text-slate-400">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
